import { memo } from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';

export type BottomSideBorderProps = {
  color: string;
  width: number;
  height: number;
  radius: number;
  edgeWidth: number;
  tipWidth: number;
  steps: number;
  side?: 'bottom' | 'left';
};

type Point = [number, number];

const staticStyles = StyleSheet.create({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

function buildBottomBorderPath(
  width: number,
  height: number,
  radius: number,
  edgeWidth: number,
  tipWidth: number,
  steps: number,
) {
  const rightOrigin: Point = [width - radius, height - radius];
  const leftOrigin: Point = [radius, height - radius];
  const clampedSteps = Math.max(1, steps);

  const rightPoint = (deg: number, rad: number): Point => {
    const t = (deg * Math.PI) / 180;
    return [rightOrigin[0] + rad * Math.sin(t), rightOrigin[1] + rad * Math.cos(t)];
  };
  const leftPoint = (deg: number, rad: number): Point => {
    const t = (deg * Math.PI) / 180;
    return [leftOrigin[0] - rad * Math.cos(t), leftOrigin[1] + rad * Math.sin(t)];
  };
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const angles = Array.from({ length: clampedSteps + 1 }, (_, i) => (90 * i) / clampedSteps);

  const left = angles.map(th => {
    const w = lerp(tipWidth, edgeWidth, th / 90);
    return {
      outer: leftPoint(th, radius),
      inner: leftPoint(th, Math.max(radius - w, 0)),
    };
  });
  const right = angles.map(th => {
    const w = lerp(edgeWidth, tipWidth, th / 90);
    return {
      outer: rightPoint(th, radius),
      inner: rightPoint(th, Math.max(radius - w, 0)),
    };
  });

  const format = ([x, y]: Point) =>
    `${Math.max(0, Math.min(width, x)).toFixed(2)},${Math.max(0, Math.min(height, y)).toFixed(2)}`;
  let d = `M${format(left[0].outer)} `;
  left.slice(1).forEach(p => {
    d += `L${format(p.outer)} `;
  });
  right.forEach(p => {
    d += `L${format(p.outer)} `;
  });
  d += `L${format(right[right.length - 1].inner)} `;
  [...right]
    .reverse()
    .slice(1)
    .forEach(p => {
      d += `L${format(p.inner)} `;
    });
  [...left]
    .reverse()
    .slice(1)
    .forEach(p => {
      d += `L${format(p.inner)} `;
    });
  return `${d}Z`;
}

export const BottomSideBorder = memo(
  ({
    color,
    width,
    height,
    radius,
    edgeWidth,
    tipWidth,
    steps,
    side = 'bottom',
  }: BottomSideBorderProps) => {
    if (width <= 0 || height <= 0) return null;

    const isLeftSide = side === 'left';
    const path = buildBottomBorderPath(
      isLeftSide ? height : width,
      isLeftSide ? width : height,
      radius,
      edgeWidth,
      tipWidth,
      steps,
    );
    const pathTransform = isLeftSide ? `matrix(0 1 -1 0 ${width} 0)` : undefined;

    return (
      <Svg
        pointerEvents="none"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={staticStyles.root}>
        <Path d={path} fill={color} transform={pathTransform} />
      </Svg>
    );
  },
);

export default BottomSideBorder;
